// `vendor/custom-radios-checkboxes/custom-radios-checkboxes.js:48` — the `<checkbox>` element
// the shortlist uses everywhere it needs one.
//
// ⚠️ IT IS A VENDORED DIRECTIVE WITH A COMPILED TEMPLATE, which is why the markup is not
// obvious from any call site. `<checkbox class="x" reset-style>` compiles to:
//
//   <label class="ng-checkbox-clean checkbox-{{model}} focused-{{guid}} x">
//     <input class="ng-checkbox-input" type="checkbox" …>
//     <div class="ng-checkbox-indication no-select agency-fp-checkbox"></div>
//     <span class="ng-transclude no-select"> </span>
//   </label>
//
// Three things in that are easy to miss and all three are visible:
//
//   - `reset-style` picks `ng-checkbox-clean` INSTEAD OF `ng-checkbox`; it is the presence of
//     the attribute that matters, not a value.
//   - the box the user sees is the `ng-checkbox-indication` DIV, not the input — the input is
//     styled away, and rendering only an `<input type="checkbox">` gives the browser's own box
//     where production has a drawn one.
//   - `replace: true` means the caller's class lands on the LABEL, alongside the directive's.
//
// The `focused-{{guid}}` class is a per-instance focus flag. It is reproduced as a `:focus`
// concern instead: the generated name is random per compile, so no stylesheet can target a
// specific one, and the rules that exist key off the `focused-` prefix through the input's
// focus state.
import { useId, type ReactElement, type ReactNode } from 'react'

export interface WooCheckboxProps {
  checked: boolean
  /** Upstream's `reset-style` attribute — presence, not value. */
  resetStyle?: boolean
  disabled?: boolean
  /** The caller's class, which `replace: true` merges onto the label. */
  className?: string
  name?: string
  tabIndex?: number
  onChange?: (checked: boolean) => void
  /** `stop-propagation` — the shortlist rows need the click not to reach the row. */
  stopPropagation?: boolean
  children?: ReactNode
}

export function WooCheckbox({
  checked,
  resetStyle = false,
  disabled = false,
  className,
  name,
  tabIndex,
  onChange,
  stopPropagation = false,
  children,
}: WooCheckboxProps): ReactElement {
  const id = useId()
  return (
    <label
      className={[resetStyle ? 'ng-checkbox-clean' : 'ng-checkbox', `checkbox-${String(checked)}`, className]
        .filter(Boolean)
        .join(' ')}
      htmlFor={id}
      onClick={(event) => {
        if (stopPropagation) event.stopPropagation()
      }}
    >
      <input
        id={id}
        className="ng-checkbox-input"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        name={name}
        tabIndex={tabIndex}
        onChange={(event) => onChange?.(event.target.checked)}
      />
      {/* The box the user actually sees. */}
      <div className="ng-checkbox-indication no-select agency-fp-checkbox" />
      <span className="ng-transclude no-select"> {children}</span>
    </label>
  )
}
